// Copyright (c) Microsoft Corporation.
// Licensed under the MIT license.

import { FormEvent, useState } from 'react';
import { MessageBar, MessageBarType, PrimaryButton, Stack, TextField } from '@fluentui/react';
import { Box } from '@mui/material';
import { MainLogo } from '../components/constants';
import * as styles from '../styles/LoginForm.styles';

type LoginFormProps = {
  // callback on successful login
  onLogin?: (user: { email: string; role: string }) => void;
  onFail?: (error: any) => void;
};

type LoginResponse = {
  isAuthenticated?: boolean;
  user?: { email: string; role: string };
  error?: string;
};

export const LoginForm = ({ onLogin, onFail }: LoginFormProps): JSX.Element => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isDisabled = !email.trim() || !password.trim();

  const handleSubmit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    if (isDisabled || isSubmitting) {
      return;
    }
    setSubmitted(false);
    setError('');
    setIsSubmitting(true);

    try {
      const response = await fetch('/api/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email, password })
      });

      const data = (await response.json()) as LoginResponse;
      if (!response.ok || !data?.isAuthenticated) {
        throw new Error(data?.error ?? 'Invalid login');
      }

      setSubmitted(true);
      if (data.user) {
        onLogin?.(data.user); // callback/notification that user has successfully logged in
      } else {
        onFail?.('Unable to identify user');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to authenticate.');
      onFail?.(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box style={styles.loginFormContainer}>
      <div style={styles.loginFormSection}>
        <form onSubmit={handleSubmit} noValidate style={styles.submitForm}>
          <Stack tokens={{ childrenGap: 12 }} styles={{ root: { minWidth: 320, maxWidth: 400 } }}>
            <div style={styles.signInHeading}>
              <MainLogo />
            </div>
            <TextField
              label="Email"
              type="email"
              value={email}
              onChange={(_, value) => setEmail(value ?? '')}
              required
            />
            <TextField
              label="Password"
              type="password"
              canRevealPassword
              value={password}
              onChange={(_, value) => setPassword(value ?? '')}
              required
            />
            <PrimaryButton
              style={styles.userLoginBtn}
              type="submit"
              text={isSubmitting ? 'Signing in...' : 'Log in'}
              disabled={isDisabled || isSubmitting}
            />
            {error && (
              <MessageBar messageBarType={MessageBarType.error} isMultiline={false}>
                {error}
              </MessageBar>
            )}
            {submitted && (
              <MessageBar messageBarType={MessageBarType.success} isMultiline={false}>
                Logged in as {email}
              </MessageBar>
            )}
          </Stack>
        </form>
      </div>
    </Box>
  );
};
